import React, { useState, useEffect } from 'react';
import supabase from '../../../../supabaseClient';
import { useAuth } from '../../../../contexts/authContext';
import './StudentCoursesPanel.css';

const StudentCoursesPanel = ({ userId }) => {
  const { user } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('enrolled');
  const [searchTerm, setSearchTerm] = useState('');

  const currentUserId = userId || user?.id;

  useEffect(() => {
    if (currentUserId) {
      fetchStudentCourses();
    }
  }, [currentUserId]);

  const fetchStudentCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch enrolled courses using the new view
      const { data: enrolledData, error: enrolledError } = await supabase
        .from('academy_enrollments')
        .select(`
          *,
          academy_courses_with_names(*)
        `)
        .eq('student_id', currentUserId)
        .eq('status', 'active');

      if (enrolledError) throw enrolledError;

      // Fetch available courses (not enrolled)
      const enrolledCourseIds = enrolledData?.map(e => e.course_id) || [];
      
      let availableQuery = supabase
        .from('academy_courses')
        .select(`
          *,
          profiles:teacher_id(id, display_name, email),
          academy_enrollments(count)
        `)
        .eq('is_published', true);

      if (enrolledCourseIds.length > 0) {
        availableQuery = availableQuery.not('id', 'in', `(${enrolledCourseIds.join(',')})`);
      }

      const { data: availableData, error: availableError } = await availableQuery;
      
      if (availableError) throw availableError;

      setEnrolledCourses(enrolledData || []);
      setAvailableCourses(availableData || []);
    } catch (err) {
      console.error('Error fetching student courses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollment = async (courseId) => {
    try {
      const { error } = await supabase
        .from('academy_enrollments')
        .insert({
          student_id: currentUserId,
          course_id: courseId,
          status: 'active',
          enrollment_date: new Date().toISOString()
        });

      if (error) throw error;
      
      await fetchStudentCourses();
    } catch (err) {
      console.error('Error enrolling in course:', err);
      setError(err.message);
    }
  };

  const filteredCourses = (activeTab === 'enrolled' ? enrolledCourses : availableCourses)
    .filter(item => {
      const course = activeTab === 'enrolled' ? item.academy_courses : item;
      return course?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             course?.description?.toLowerCase().includes(searchTerm.toLowerCase());
    });

  const renderEnrolledCourseCard = (enrollment) => {
    const course = enrollment.academy_courses;
    const progress = enrollment.academy_user_progress?.[0];
    
    return (
      <div key={enrollment.id} className="student-course-card enrolled">
        <div className="course-header">
          <h3>{course.title}</h3>
          <div className="progress-badge">
            {progress?.progress_percentage || 0}% Complete
          </div>
        </div>
        
        <div className="course-details">
          <p className="course-description">{course.description}</p>
          
          <div className="course-meta">
            <div className="meta-item">
              <span className="label">Language:</span>
              <span className="value">{course.language}</span>
            </div>
            <div className="meta-item">
              <span className="label">Level:</span>
              <span className="value">{course.level}</span>
            </div>
            <div className="meta-item">
              <span className="label">Teacher:</span>
              <span className="value">{course.profiles?.display_name}</span>
            </div>
          </div>

          {progress && (
            <div className="progress-details">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress.progress_percentage || 0}%` }}
                ></div>
              </div>
              <div className="progress-stats">
                <span>{progress.completed_lessons || 0} / {progress.total_lessons || 0} lessons</span>
              </div>
            </div>
          )}
        </div>

        <div className="course-actions">
          <button 
            className="btn-primary"
            onClick={() => window.open(`/app/academy/lms/courses/${course.id}/learn`, '_blank')}
          >
            Continue Learning
          </button>
          <button 
            className="btn-outline"
            onClick={() => window.open(`/app/academy/lms/courses/${course.id}`, '_blank')}
          >
            Course Details
          </button>
        </div>
      </div>
    );
  };

  const renderAvailableCourseCard = (course) => (
    <div key={course.id} className="student-course-card available">
      <div className="course-header">
        <h3>{course.title}</h3>
        <div className="availability-badge">
          Available
        </div>
      </div>
      
      <div className="course-details">
        <p className="course-description">{course.description}</p>
        
        <div className="course-meta">
          <div className="meta-item">
            <span className="label">Language:</span>
            <span className="value">{course.language}</span>
          </div>
          <div className="meta-item">
            <span className="label">Level:</span>
            <span className="value">{course.level}</span>
          </div>
          <div className="meta-item">
            <span className="label">Duration:</span>
            <span className="value">{course.duration_weeks} weeks</span>
          </div>
          <div className="meta-item">
            <span className="label">Teacher:</span>
            <span className="value">{course.profiles?.username}</span>
          </div>
        </div>

        <div className="enrollment-info">
          <span className="enrollment-count">
            {course.academy_enrollments?.[0]?.count || 0} students enrolled
          </span>
        </div>
      </div>

      <div className="course-actions">
        <button 
          className="btn-primary"
          onClick={() => handleEnrollment(course.id)}
        >
          Enroll Now
        </button>
        <button 
          className="btn-outline"
          onClick={() => window.open(`/app/academy/lms/courses/${course.id}`, '_blank')}
        >
          Preview Course
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="student-courses-panel">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading your courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-courses-panel">
        <div className="error-state">
          <h3>Error Loading Courses</h3>
          <p>{error}</p>
          <button onClick={fetchStudentCourses} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="student-courses-panel">
      <div className="panel-header">
        <h2>My Courses</h2>
        <p>Track your learning progress and discover new courses</p>
      </div>

      <div className="learning-stats">
        <div className="stat-card">
          <h4>Enrolled Courses</h4>
          <span className="stat-number">{enrolledCourses.length}</span>
        </div>
        <div className="stat-card">
          <h4>Average Progress</h4>
          <span className="stat-number">
            {enrolledCourses.length > 0 
              ? Math.round(enrolledCourses.reduce((acc, e) => 
                  acc + (e.academy_user_progress?.[0]?.progress_percentage || 0), 0) / enrolledCourses.length)
              : 0}%
          </span>
        </div>
        <div className="stat-card">
          <h4>Available Courses</h4>
          <span className="stat-number">{availableCourses.length}</span>
        </div>
      </div>

      <div className="course-tabs">
        <button 
          className={`tab-button ${activeTab === 'enrolled' ? 'active' : ''}`}
          onClick={() => setActiveTab('enrolled')}
        >
          My Enrolled Courses ({enrolledCourses.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'available' ? 'active' : ''}`}
          onClick={() => setActiveTab('available')}
        >
          Available Courses ({availableCourses.length})
        </button>
      </div>

      <div className="course-search">
        <input
          type="text"
          placeholder={`Search ${activeTab} courses...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="courses-grid">
        {filteredCourses.length > 0 ? (
          filteredCourses.map(item => 
            activeTab === 'enrolled' 
              ? renderEnrolledCourseCard(item)
              : renderAvailableCourseCard(item)
          )
        ) : (
          <div className="empty-state">
            <h3>
              {activeTab === 'enrolled' 
                ? 'No Enrolled Courses' 
                : 'No Available Courses'}
            </h3>
            <p>
              {activeTab === 'enrolled' 
                ? 'Enroll in a course to start your learning journey!'
                : 'Check back later for new course offerings.'}
            </p>
            {activeTab === 'enrolled' && (
              <button 
                onClick={() => setActiveTab('available')}
                className="btn-primary"
              >
                Browse Available Courses
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentCoursesPanel;
